import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import find from 'lodash/find';
import { ButtonWithIcon } from '@els/els-react--button';
import { Card } from '@els/els-react--card';
import FactsContext from '@Components/Results/substances/contexts/facts.context';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import SubstanceProperty from '@Components/Results/substances/SubstanceProperty';
import {
  getChildCount,
  getChildHitCount,
  getFactCount,
  isChildCommercialSuppliers,
} from '@Components/Results/substances/utils/factCountHelper';
import NumberFormatter from '@Utils/NumberFormatter';
import Slugify from '@Utils/Slugify';

function findHitDataFact(facts) {
  return find(facts, (fact) => fact.hitData);
}

function SubstanceHitData({ metadata, substance, database }) {
  const { index: substanceIndex, details } = substance;
  const { facts, toggle } = useContext(FactsContext);
  const fact = findHitDataFact(facts);
  const commercialSupplierChild = useMemo(() => find(fact?.children || [], isChildCommercialSuppliers), [fact]);

  const heading = useMemo(() => {
    if (!fact) return '';
    if (commercialSupplierChild) {
      return `${commercialSupplierChild.title || 'Commercial Suppliers'} - ${NumberFormatter(
        getChildHitCount(commercialSupplierChild),
        0
      )} hits out of ${NumberFormatter(getChildCount(commercialSupplierChild), 0)}`;
    }
    const count = getFactCount(fact, substance);
    const title = fact.title || 'Hit data';
    return count ? `${title} - ${NumberFormatter(count, 0)}` : title;
  }, [commercialSupplierChild, fact, substance]);

  if (!fact?.open) return null;

  const headingId = `substance-hitdata-card-heading-${Slugify(fact.id || fact.title || 'hit-data')}-${substanceIndex}`;

  return (
    <div id={`substances-hitdata-${substanceIndex}`} className="substance-results-information substances-hit-details">
      <div className="substances-hit-details__container">
        <Card
          className="substances-hit-details__card"
          heading={
            <div className="substances-hit-details__header">
              <h3 id={headingId} className="u-els-font-size-base substances-hit-details__title">
                {heading}
              </h3>
              <ButtonWithIcon
                data-e2e="substance-hitdata-close"
                type="borderless"
                size="x-small"
                sprite="Close"
                iconRight
                iconOnly
                className="substances-hit-details__close"
                aria-label={`Close ${heading}`}
                onClick={() => toggle?.(fact.id, false)}>
                Close {heading}
              </ButtonWithIcon>
            </div>
          }
          headingLevel="h3"
          id={`substance-hitdata-card-${substanceIndex}`}
          // RX-23573: no `role="region"` — per-result panel landmark overuse (ARIA APG). `<h3>` heading retained.
          aria-labelledby={headingId}>
          <SubstanceProperty
            metadata={metadata}
            fact={fact}
            details={details}
            database={database}
            substanceIndex={substanceIndex}
            substance={substance}
            isTabPanel
          />
        </Card>
      </div>
    </div>
  );
}

SubstanceHitData.propTypes = {
  metadata: MetadataPropTypesShape,
  substance: PropTypes.object.isRequired,
  database: PropTypes.object.isRequired,
};

export default SubstanceHitData;
